import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';

import { Plus, Trash2, GripVertical, Save, Sparkles, Loader2, ImagePlus, X as XIcon, Link2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import Logotype from '@/components/shared/Logotype';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const S = {
  bg: '#0a0a0f',
  cream: '#E8E3D9',
  secondary: '#b0b4c0',
  border: 'rgba(255,255,255,0.06)',
  serif: "'Cormorant Garamond', serif",
  mono: "'DM Mono', monospace",
};

function CE({ tag: Tag = 'p', value, onChange, style, placeholder, className, onClick }) {
  const ref = useRef();
  // Seed DOM on mount
  useEffect(() => {
    if (ref.current) ref.current.innerText = value || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Sync DOM when value changes externally (e.g. after Research & Rewrite),
  // but never overwrite while the field is focused (user is mid-edit)
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.innerText = value || '';
    }
  }, [value]);
  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={e => onChange(e.currentTarget.innerText)}
      onClick={onClick}
      data-placeholder={placeholder}
      className={className}
      style={{ outline: 'none', minHeight: '1em', ...style }}
    />
  );
}

function PhotoDropZone({ url, onUpload, height = 340, label = 'Drop image or click', slug = 'capetown', sectionId, sectionTitle }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState('find');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [finding, setFinding] = useState(false);
  const [findResults, setFindResults] = useState([]);
  const [webQuery, setWebQuery] = useState('');
  const [webResults, setWebResults] = useState([]);
  const [webSearching, setWebSearching] = useState(false);
  const [webSaving, setWebSaving] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collPhotos, setCollPhotos] = useState([]);
  const [activeColl, setActiveColl] = useState(null);
  const [loadingColl, setLoadingColl] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'ok'|'fail', text: string }
  const inputRef = useRef();
  const statusTimer = useRef();

  const flash = (type, text) => {
    clearTimeout(statusTimer.current);
    setStatus({ type, text });
    statusTimer.current = setTimeout(() => setStatus(null), 2400);
  };

  const upload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('param', label === 'hero' ? 'hero' : label);
    try {
      const { data } = await api.post(`/curated/${slug}/upload-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUpload(data.url);
      setPickerOpen(false);
    } catch { flash('fail', 'Upload failed'); }
    setUploading(false);
  };

  const searchPhotos = async (q) => {
    setSearching(true);
    try {
      const { data } = await api.get(`/curated/photos/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data);
    } catch { flash('fail', 'Search failed'); }
    setSearching(false);
  };

  const findPhotos = async () => {
    setFinding(true);
    try {
      const { data } = await api.post(`/curated/${slug}/find-photos`, {
        section_id: sectionId,
        query: sectionTitle || '',
      });
      setFindResults(data.photos || []);
    } catch { flash('fail', 'Search failed'); }
    setFinding(false);
  };

  const searchWeb = async (q) => {
    if (!q.trim()) return;
    setWebSearching(true);
    try {
      const { data } = await api.post(`/curated/${slug}/web-photos`, {
        query: q,
        section_title: sectionTitle || '',
      });
      setWebResults(data.images || []);
    } catch { flash('fail', 'Web search failed'); }
    setWebSearching(false);
  };

  const saveWebPhoto = async (imageUrl) => {
    setWebSaving(imageUrl);
    try {
      const { data } = await api.post(`/curated/${slug}/save-web-photo`, { url: imageUrl });
      onUpload(data.url);
      setPickerOpen(false);
    } catch { flash('fail', 'Failed to save'); }
    setWebSaving(null);
  };

  const loadCollections = async () => {
    if (collections.length > 0) return;
    try {
      const { data } = await api.get('/framed/curated');
      // Prepend Flow as a virtual collection
      setCollections([{ id: '__flow__', title: 'Flow', description: 'Unreel' }, ...(data || [])]);
    } catch { flash('fail', 'Failed to load'); }
  };

  const openCollection = async (coll) => {
    setActiveColl(coll);
    setLoadingColl(true);
    try {
      if (coll.id === '__flow__') {
        const { data } = await api.get('/photos');
        const photos = Array.isArray(data) ? data : [];
        setCollPhotos(photos);
      } else {
        const { data } = await api.get(`/framed/curated/${coll.id}`);
        setCollPhotos(data.photos || []);
      }
    } catch { flash('fail', 'Failed to load'); }
    setLoadingColl(false);
  };

  const selectPhoto = (photo) => {
    onUpload(photo.url);
    setPickerOpen(false);
  };

  const openPicker = (e) => {
    e.stopPropagation();
    setPickerOpen(true);
    setPickerTab('find');
    setFindResults([]);
    setSearchResults([]);
    setSearchQuery('');
    setWebResults([]);
    setWebQuery(sectionTitle || '');
    setActiveColl(null);
    setCollPhotos([]);
    loadCollections();
  };

  const photoGrid = (photos) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
      {photos.map(p => (
        <div key={p.id} onClick={() => selectPhoto(p)} style={{ cursor: 'pointer', position: 'relative', aspectRatio: '1', overflow: 'hidden', border: `1px solid ${S.border}` }}>
          <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {(p.location || p.tagline) && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.7)', fontSize: 9, fontFamily: S.mono, color: S.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.location || p.tagline}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ position: 'relative', height: pickerOpen ? 'auto' : height, minHeight: height, overflow: 'hidden' }}>
      {/* Image / empty state */}
      <div
        style={{ position: 'relative', height, overflow: 'hidden', cursor: 'pointer', border: dragging ? `1px dashed ${S.secondary}` : 'none', transition: 'border 0.2s' }}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
        onClick={() => { if (!pickerOpen) inputRef.current?.click(); }}>
        {url ? (
          <img
            src={url}
            alt=""
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              // contain so the whole photo is visible — cover was silently
              // cropping portraits and wide landscapes in the editor.
              objectFit: 'contain',
              background: '#0a0a0f',
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontFamily: S.mono, fontSize: 10, color: S.secondary, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{uploading ? 'Uploading...' : label}</p>
          </div>
        )}
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontFamily: S.mono, fontSize: 10, color: S.cream }}>Uploading...</p>
          </div>
        )}
        {/* Find photos button */}
        <button
          onClick={openPicker}
          style={{
            position: 'absolute', bottom: 10, right: 10, zIndex: 3,
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(107,53,184,0.85)', border: 'none', borderRadius: 16,
            color: '#fff', fontFamily: S.mono, fontSize: 9, letterSpacing: '0.1em',
            textTransform: 'uppercase', padding: '6px 14px', cursor: 'pointer',
            backdropFilter: 'blur(8px)',
          }}>
          <Sparkles size={10} /> Find Photos
        </button>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ''; }} />
      </div>

      {/* Photo picker overlay */}
      {pickerOpen && (
        <div style={{
          background: 'rgba(10,10,15,0.95)', padding: 16, marginTop: -1, transition: 'border-color 0.3s',
          border: `1px solid ${status?.type === 'fail' ? 'rgba(185,40,40,0.6)' : status?.type === 'ok' ? 'rgba(40,185,80,0.6)' : S.border}`,
        }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: `1px solid ${S.border}` }}>
            {[{ id: 'find', label: 'Zephyr Find' }, { id: 'collections', label: 'Collections' }, { id: 'web', label: 'Web' }, { id: 'search', label: 'Library' }, { id: 'upload', label: 'Upload' }].map(t => (
              <button key={t.id} onClick={() => setPickerTab(t.id)} style={{
                background: 'none', border: 'none', borderBottom: pickerTab === t.id ? '2px solid #6B35B8' : '2px solid transparent',
                color: pickerTab === t.id ? S.cream : S.secondary, fontFamily: S.mono, fontSize: 10,
                letterSpacing: '0.12em', textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s',
              }}>{t.label}</button>
            ))}
            <div style={{ flex: 1 }} />
            {status && (
              <span style={{
                fontFamily: S.mono, fontSize: 10, letterSpacing: '0.08em',
                color: status.type === 'fail' ? '#b92828' : '#28b950',
                padding: '8px 10px', transition: 'opacity 0.3s',
              }}>
                {status.type === 'fail' ? '✗' : '✓'} {status.text}
              </span>
            )}
            <button onClick={() => setPickerOpen(false)} style={{ background: 'none', border: 'none', color: S.secondary, cursor: 'pointer', padding: '8px 12px', fontSize: 14 }}>x</button>
          </div>

          {/* Zephyr Find tab */}
          {pickerTab === 'find' && (
            <div>
              <p style={{ fontFamily: S.serif, fontSize: 14, color: S.secondary, marginBottom: 10 }}>
                Zephyr will search your photo library for images that match "{sectionTitle || 'this section'}".
              </p>
              <button
                onClick={findPhotos}
                disabled={finding}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: finding ? '#4a1f8a' : '#6B35B8', border: 'none', borderRadius: 20,
                  color: '#fff', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em',
                  textTransform: 'uppercase', padding: '8px 20px',
                  cursor: finding ? 'default' : 'pointer',
                  opacity: finding ? 0.7 : 1,
                }}>
                {finding ? <><Loader2 size={12} className="animate-spin" /> Searching...</> : <><Sparkles size={12} /> Find matching photos</>}
              </button>
              {findResults.length > 0 && photoGrid(findResults)}
            </div>
          )}

          {/* Search tab */}
          {pickerTab === 'search' && (
            <div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') searchPhotos(searchQuery); }}
                  placeholder="Search by location, tagline..."
                  style={{
                    flex: 1, background: 'rgba(255,255,255,0.02)', border: `1px solid ${S.border}`,
                    color: S.cream, fontFamily: S.mono, fontSize: 12, padding: '8px 12px', outline: 'none',
                  }}
                  autoFocus
                />
                <button
                  onClick={() => searchPhotos(searchQuery)}
                  disabled={searching}
                  style={{
                    background: '#6B35B8', border: 'none', borderRadius: 16,
                    color: '#fff', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.1em',
                    textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer',
                  }}>
                  {searching ? '...' : 'Search'}
                </button>
              </div>
              {searchResults.length > 0 && photoGrid(searchResults)}
            </div>
          )}

          {/* Collections tab */}
          {pickerTab === 'collections' && (
            <div>
              {!activeColl ? (
                <>
                  <p style={{ fontFamily: S.serif, fontSize: 14, color: S.secondary, marginBottom: 12 }}>Select a collection to browse its photos.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {collections.map(c => (
                      <button
                        key={c.id}
                        onClick={() => openCollection(c)}
                        style={{
                          background: 'rgba(255,255,255,0.02)', border: `1px solid ${S.border}`,
                          color: S.cream, fontFamily: S.serif, fontSize: 15, padding: '14px 16px',
                          cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = S.secondary}
                        onMouseLeave={e => e.currentTarget.style.borderColor = S.border}>
                        {c.title}
                        {c.description && <span style={{ display: 'block', fontSize: 11, fontFamily: S.mono, color: S.secondary, marginTop: 4 }}>{c.description}</span>}
                      </button>
                    ))}
                  </div>
                  {collections.length === 0 && <p style={{ fontFamily: S.mono, fontSize: 11, color: S.secondary }}>No collections found.</p>}
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setActiveColl(null); setCollPhotos([]); }}
                    style={{ background: 'none', border: 'none', color: S.secondary, fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                    &larr; {activeColl.title}
                  </button>
                  {loadingColl ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                      <Loader2 size={16} className="animate-spin" style={{ color: S.secondary }} />
                    </div>
                  ) : (
                    photoGrid(collPhotos)
                  )}
                  {!loadingColl && collPhotos.length === 0 && <p style={{ fontFamily: S.mono, fontSize: 11, color: S.secondary, marginTop: 8 }}>No photos in this collection.</p>}
                </>
              )}
            </div>
          )}

          {/* Web tab */}
          {pickerTab === 'web' && (
            <div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={webQuery}
                  onChange={e => setWebQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') searchWeb(webQuery); }}
                  placeholder="Search the web for images..."
                  style={{
                    flex: 1, background: 'rgba(255,255,255,0.02)', border: `1px solid ${S.border}`,
                    color: S.cream, fontFamily: S.mono, fontSize: 12, padding: '8px 12px', outline: 'none',
                  }}
                  autoFocus
                />
                <button
                  onClick={() => searchWeb(webQuery)}
                  disabled={webSearching || !webQuery.trim()}
                  style={{
                    background: '#6B35B8', border: 'none', borderRadius: 16,
                    color: '#fff', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.1em',
                    textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer',
                    opacity: !webQuery.trim() ? 0.4 : 1,
                  }}>
                  {webSearching ? <Loader2 size={12} className="animate-spin" /> : 'Search'}
                </button>
              </div>
              {webResults.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
                  {webResults.map((img, i) => (
                    <div key={i} onClick={() => saveWebPhoto(img.thumb || img.url)} style={{
                      cursor: webSaving ? 'wait' : 'pointer', position: 'relative', aspectRatio: '1', overflow: 'hidden',
                      border: `1px solid ${S.border}`, opacity: webSaving === (img.thumb || img.url) ? 0.5 : 1,
                    }}>
                      <img src={img.thumb || img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {webSaving === (img.thumb || img.url) && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
                          <Loader2 size={16} className="animate-spin" style={{ color: '#fff' }} />
                        </div>
                      )}
                      {img.source && (
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.7)', fontSize: 9, fontFamily: S.mono, color: S.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {img.source}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upload tab */}
          {pickerTab === 'upload' && (
            <div
              style={{
                border: `1px dashed ${S.border}`, padding: 32, textAlign: 'center', cursor: 'pointer',
              }}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
              onClick={() => inputRef.current?.click()}>
              <p style={{ fontFamily: S.mono, fontSize: 10, color: S.secondary, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {uploading ? 'Uploading...' : 'Drop image or click to upload'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SortableEntry({ entry, sectionTitle, guideTitle, guideSubtitle, slug, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: entry.id });
  const dndStyle = { transform: CSS.Transform.toString(transform), transition };
  const [researching, setResearching] = useState(false);
  const [local, setLocal] = useState(entry);
  const [entryDirty, setEntryDirty] = useState(false);
  const [saving, setEntrySaving] = useState(false);
  const [researchFailed, setResearchFailed] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const entryPhotoRef = useRef();

  // Sync local state when entry changes externally (e.g. after Research & Rewrite)
  useEffect(() => {
    setLocal(entry);
    setEntryDirty(false);
  }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = (field, val) => {
    setLocal(prev => ({ ...prev, [field]: val }));
    setEntryDirty(true);
  };

  const saveEntry = async () => {
    setEntrySaving(true);
    onUpdate(local);
    // brief visual confirmation before clearing dirty flag
    setTimeout(() => { setEntryDirty(false); setEntrySaving(false); }, 400);
  };

  const runResearch = async () => {
    setResearching(true);
    try {
      const { data } = await api.post('/curated/entries/research', {
        name: local.name,
        note: local.note,
        url: local.url,
        section_title: sectionTitle,
        guide_title: guideTitle,
        guide_subtitle: guideSubtitle,
      });
      onUpdate({ ...entry, name: data.name, note: data.note });
      // rewritten
    } catch {
      setResearchFailed(true);
      setTimeout(() => setResearchFailed(false), 2400);
    }
    setResearching(false);
  };

  const openLink = () => { if (local.url) window.open(local.url, '_blank', 'noopener,noreferrer'); };
  const stop = e => e.stopPropagation();

  const uploadEntryPhoto = async (file) => {
    if (!file) return;
    setImageUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('param', `entry-${entry.id}`);
    try {
      const { data } = await api.post(`/curated/${slug}/upload-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Persist immediately so the photo survives even if the user doesn't click Save
      onUpdate({ ...entry, image_url: data.url });
      setLocal(prev => ({ ...prev, image_url: data.url }));
    } catch {
      // silent fail — the drop zone stays empty
    }
    setImageUploading(false);
  };

  const removeEntryPhoto = () => {
    onUpdate({ ...entry, image_url: '' });
    setLocal(prev => ({ ...prev, image_url: '' }));
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...dndStyle,
        borderBottom: `1px solid ${S.border}`,
        padding: '20px 0',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        cursor: local.url ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
      onClick={openLink}
      onMouseEnter={e => { if (local.url) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Drag handle */}
      <div {...listeners} {...attributes} onClick={stop} style={{ cursor: 'grab', color: S.border, paddingTop: 4, flexShrink: 0 }}>
        <GripVertical size={14} />
      </div>

      {/* Content — stop propagation so editing doesn't trigger navigation */}
      <div style={{ flex: 1, minWidth: 0 }} onClick={stop}>
        <CE
          tag="p"
          value={local.name}
          onChange={v => handleField('name', v)}
          placeholder="Entry name"
          style={{ fontFamily: S.serif, fontSize: 22, color: S.cream, marginBottom: 8 }}
        />
        <CE
          tag="p"
          value={local.note}
          onChange={v => handleField('note', v)}
          placeholder="Note…"
          style={{ fontFamily: S.serif, fontSize: 15, fontStyle: 'italic', color: S.secondary, lineHeight: 1.7 }}
        />
        <div style={{ marginTop: 8 }}>
          <input
            type="text"
            value={local.url || ''}
            onChange={e => handleField('url', e.target.value)}
            placeholder="URL"
            style={{ background: 'transparent', border: 'none', borderBottom: `1px solid ${S.border}`, color: S.secondary, fontFamily: S.mono, fontSize: 11, padding: '2px 0', outline: 'none', width: '100%' }}
          />
        </div>
        {/* Entry photo — natural aspect ratio, no cropping. Click the placeholder to upload. */}
        {local.image_url ? (
          <div style={{ marginTop: 12, position: 'relative', background: '#0a0a0f', border: `1px solid ${S.border}` }}>
            <img
              src={local.image_url}
              alt=""
              style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 420, objectFit: 'contain', background: '#0a0a0f' }}
            />
            <button
              onClick={(e) => { stop(e); removeEntryPhoto(); }}
              title="Remove photo"
              style={{
                position: 'absolute', top: 8, right: 8,
                background: 'rgba(0,0,0,0.7)', border: `1px solid ${S.border}`, color: S.cream,
                cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center',
              }}>
              <XIcon size={11} />
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <button
              onClick={(e) => { stop(e); entryPhotoRef.current?.click(); }}
              disabled={imageUploading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'none', border: `1px dashed ${S.border}`, color: S.secondary,
                fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                padding: '5px 12px', cursor: imageUploading ? 'default' : 'pointer',
              }}
              onMouseEnter={e => { if (!imageUploading) e.currentTarget.style.borderColor = S.secondary; }}
              onMouseLeave={e => e.currentTarget.style.borderColor = S.border}>
              {imageUploading ? <Loader2 size={11} className="animate-spin" /> : <ImagePlus size={11} />}
              {imageUploading ? 'Uploading...' : 'Add photo'}
            </button>
            <input
              ref={entryPhotoRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadEntryPhoto(f); }}
            />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            onClick={runResearch}
            disabled={researching || researchFailed}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: researchFailed ? '#6b1a1a' : researching ? '#4a1f8a' : '#6B35B8', border: 'none', borderRadius: 20,
              color: '#fff', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em',
              textTransform: 'uppercase', padding: '5px 14px', cursor: researching ? 'default' : 'pointer',
              opacity: researching ? 0.7 : 1, transition: 'background 0.2s',
            }}>
            <Sparkles size={11} />
            {researchFailed ? '✗ Failed' : researching ? 'Researching...' : 'Research & Rewrite'}
          </button>
          {entryDirty && (
            <button
              onClick={saveEntry}
              disabled={saving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent', border: `1px solid ${S.border}`, borderRadius: 20,
                color: saving ? S.secondary : S.cream, fontFamily: S.mono, fontSize: 10,
                letterSpacing: '0.12em', textTransform: 'uppercase', padding: '5px 14px',
                cursor: saving ? 'default' : 'pointer', transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.borderColor = S.secondary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; }}>
              <Save size={11} />
              {saving ? 'Saved' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Delete */}
      <button
        onClick={e => { stop(e); if (window.confirm(`Delete "${local.name}"?`)) onDelete(entry.id); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.border, flexShrink: 0, paddingTop: 4 }}
        onMouseEnter={e => e.currentTarget.style.color = '#b87d7d'}
        onMouseLeave={e => e.currentTarget.style.color = S.border}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function SectionEditor({ section, slug, guideTitle, guideSubtitle, onUpdate, onDelete }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateEntry = (updated) => {
    const entries = section.entries.map(e => e.id === updated.id ? updated : e);
    onUpdate({ ...section, entries });
  };

  const deleteEntry = (entryId) => {
    const entries = section.entries.filter(e => e.id !== entryId);
    onUpdate({ ...section, entries });
  };

  const addEntry = () => {
    const newEntry = {
      id: crypto.randomUUID(),
      name: 'New entry',
      note: '',
      url: '',
      tags: [],
      order: (section.entries || []).length + 1,
    };
    onUpdate({ ...section, entries: [...(section.entries || []), newEntry] });
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = section.entries.findIndex(e => e.id === active.id);
    const newIndex = section.entries.findIndex(e => e.id === over.id);
    const reordered = arrayMove(section.entries, oldIndex, newIndex).map((e, i) => ({ ...e, order: i + 1 }));
    onUpdate({ ...section, entries: reordered });
  };

  const sortedEntries = [...(section.entries || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{ marginBottom: 64 }}>
      <PhotoDropZone
        url={section.photo_url}
        label={section.id}
        height={240}
        slug={slug}
        sectionId={section.id}
        sectionTitle={section.title}
        onUpload={url => onUpdate({ ...section, photo_url: url })}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, marginBottom: 8 }}>
        <CE
          tag="p"
          value={section.title}
          onChange={v => onUpdate({ ...section, title: v })}
          style={{ fontFamily: S.mono, fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: S.secondary }}
          placeholder="Section title"
        />
        <button
          onClick={() => { if (window.confirm(`Delete section "${section.title}"?`)) onDelete(section.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.border }}
          onMouseEnter={e => e.currentTarget.style.color = '#b87d7d'}
          onMouseLeave={e => e.currentTarget.style.color = S.border}>
          <Trash2 size={12} />
        </button>
      </div>
      <div style={{ borderTop: `1px solid ${S.border}` }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortedEntries.map(e => e.id)} strategy={verticalListSortingStrategy}>
            {sortedEntries.map(entry => (
              <SortableEntry key={entry.id} entry={entry} sectionTitle={section.title} guideTitle={guideTitle} guideSubtitle={guideSubtitle} slug={slug} onUpdate={updateEntry} onDelete={deleteEntry} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <button
        onClick={addEntry}
        style={{ marginTop: 16, background: 'none', border: `1px dashed ${S.border}`, color: S.secondary, padding: '8px 20px', cursor: 'pointer', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}
        onMouseEnter={e => e.currentTarget.style.borderColor = S.secondary}
        onMouseLeave={e => e.currentTarget.style.borderColor = S.border}>
        <Plus size={12} /> Add Entry
      </button>
    </div>
  );
}

export default function CuratedEditor() {
  const { slug = 'capetown' } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [editorStatus, setEditorStatus] = useState(null);
  const saveTimer = useRef(null);
  const editorStatusTimer = useRef();

  const editorFlash = (type, text) => {
    clearTimeout(editorStatusTimer.current);
    setEditorStatus({ type, text });
    editorStatusTimer.current = setTimeout(() => setEditorStatus(null), 2400);
  };

  useEffect(() => {
    setLoading(true);
    setGuide(null);
    api.get(`/curated/${slug}`)
      .then(r => setGuide(r.data))
      .catch(() => editorFlash('fail', 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  const updateGuide = useCallback((updated) => {
    setGuide(updated);
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(updated), 3000);
  }, []);

  const save = async (data) => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put(`/curated/${slug}`, data);
      setDirty(false);
    } catch { editorFlash('fail', 'Save failed'); }
    setSaving(false);
  };

  const saveNow = () => {
    clearTimeout(saveTimer.current);
    save(guide);
  };

  const updateSection = (updated) => {
    const sections = guide.sections.map(s => s.id === updated.id ? updated : s);
    updateGuide({ ...guide, sections });
  };

  const deleteSection = (sectionId) => {
    const sections = guide.sections.filter(s => s.id !== sectionId);
    updateGuide({ ...guide, sections });
  };

  const runBulkImport = async () => {
    if (!bulkText.trim()) return;
    setBulkImporting(true);
    try {
      const { data } = await api.post(`/curated/${slug}/bulk-import`, { text: bulkText });
      setGuide(prev => ({ ...prev, sections: data.sections }));
      setBulkText('');
      setDirty(false);
    } catch { editorFlash('fail', 'Import failed'); }
    setBulkImporting(false);
  };

  const addSection = () => {
    const newSection = {
      id: crypto.randomUUID(),
      title: 'New Section',
      photo_url: '',
      order: (guide.sections || []).length + 1,
      entries: [],
    };
    updateGuide({ ...guide, sections: [...(guide.sections || []), newSection] });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: S.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 8, height: 8, background: S.secondary, borderRadius: '50%' }} />
      </div>
    );
  }

  if (!guide) return null;

  const sections = [...(guide.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={{ minHeight: '100vh', background: S.bg, color: S.cream, fontFamily: S.serif }}>
      {/* Breadcrumb — Section 10.x layer 2 (nested view).
          Cult of One is the parent since curated guides live under the Curated tab there. */}
      <div style={{
        padding: '20px 32px',
        borderBottom: `1px solid ${S.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Breadcrumb>
          <BreadcrumbList style={{ gap: 0 }}>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/cult?tab=curated" style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                  Cult of One
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/cult?tab=curated" style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                  Curated
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
            <BreadcrumbItem>
              <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                {guide.title || 'Untitled'}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <button
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/gallery/cult/curated/${slug}`);
            editorFlash('ok', 'Link copied');
          }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7084', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}
          title="Copy shareable link"
        >
          <Link2 size={14} strokeWidth={1.5} />
          <span style={{ fontSize: 11, fontFamily: "'Inter', sans-serif", fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Share</span>
        </button>
      </div>

      {/* Hero */}
      <PhotoDropZone
        url={guide.hero_image_url}
        label="hero"
        height={480}
        slug={slug}
        onUpload={url => updateGuide({ ...guide, hero_image_url: url })}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 40px' }}>
        {/* Title + subtitle */}
        <CE
          tag="h1"
          value={guide.title}
          onChange={v => updateGuide({ ...guide, title: v })}
          style={{ fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 300, color: S.cream, marginBottom: 12, lineHeight: 1.1 }}
          placeholder="Title"
        />
        <CE
          tag="p"
          value={guide.subtitle}
          onChange={v => updateGuide({ ...guide, subtitle: v })}
          style={{ fontFamily: S.mono, fontSize: 13, color: S.secondary, letterSpacing: '0.12em', marginBottom: 32 }}
          placeholder="Subtitle"
        />

        <div style={{ borderTop: `1px solid ${S.border}`, borderBottom: `1px solid ${S.border}`, padding: '24px 0', marginBottom: 48 }}>
          <CE
            tag="p"
            value={guide.intro}
            onChange={v => updateGuide({ ...guide, intro: v })}
            style={{ fontSize: 18, lineHeight: 1.8, fontStyle: 'italic', color: S.cream }}
            placeholder="Intro paragraph…"
          />
        </div>

        {/* Bulk Import */}
        <div style={{ marginBottom: 48, border: `1px solid ${S.border}`, padding: 20 }}>
          <p style={{ fontFamily: S.mono, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: S.secondary, marginBottom: 12 }}>
            Bulk Import
          </p>
          <textarea
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            placeholder="Paste a list of places, recommendations, or raw notes. Zephyr will organize them into sections and entries."
            style={{
              width: '100%', minHeight: 100, background: 'rgba(255,255,255,0.02)', border: `1px solid ${S.border}`,
              color: S.cream, fontFamily: S.serif, fontSize: 15, lineHeight: 1.7, padding: 16, outline: 'none',
              resize: 'vertical',
            }}
          />
          <button
            onClick={runBulkImport}
            disabled={bulkImporting || !bulkText.trim()}
            style={{
              marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
              background: bulkImporting ? '#4a1f8a' : '#6B35B8', border: 'none', borderRadius: 20,
              color: '#fff', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em',
              textTransform: 'uppercase', padding: '8px 20px',
              cursor: bulkImporting || !bulkText.trim() ? 'default' : 'pointer',
              opacity: !bulkText.trim() ? 0.4 : bulkImporting ? 0.7 : 1,
              transition: 'background 0.2s, opacity 0.2s',
            }}>
            {bulkImporting ? <><Loader2 size={12} className="animate-spin" /> Importing...</> : <><Sparkles size={12} /> Let Zephyr sort it out</>}
          </button>
        </div>

        {/* Sections */}
        {sections.map(section => (
          <SectionEditor key={section.id} section={section} slug={slug} guideTitle={guide.title} guideSubtitle={guide.subtitle} onUpdate={updateSection} onDelete={deleteSection} />
        ))}

        <button
          onClick={addSection}
          style={{ marginTop: 32, background: 'none', border: `1px dashed ${S.border}`, color: S.secondary, padding: '12px 32px', cursor: 'pointer', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'center' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = S.secondary}
          onMouseLeave={e => e.currentTarget.style.borderColor = S.border}>
          <Plus size={14} /> Add Section
        </button>
      </div>

      {/* Floating save / status indicator */}
      {(dirty || editorStatus) && (
        <div style={{
          position: 'fixed', bottom: 32, right: 32, background: 'rgba(255,255,255,0.03)', padding: '10px 20px',
          display: 'flex', alignItems: 'center', gap: 10, zIndex: 100, transition: 'border-color 0.3s',
          border: `1px solid ${editorStatus?.type === 'fail' ? 'rgba(185,40,40,0.6)' : editorStatus?.type === 'ok' ? 'rgba(40,185,80,0.6)' : S.border}`,
        }}>
          {editorStatus ? (
            <span style={{ fontFamily: S.mono, fontSize: 11, letterSpacing: '0.1em', color: editorStatus.type === 'fail' ? '#b92828' : '#28b950' }}>
              {editorStatus.type === 'fail' ? '✗' : '✓'} {editorStatus.text}
            </span>
          ) : (
            <span style={{ fontFamily: S.mono, fontSize: 11, color: S.secondary, letterSpacing: '0.1em' }}>Unsaved changes</span>
          )}
          {dirty && (
            <button
              onClick={saveNow}
              disabled={saving}
              style={{ background: saving ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)', border: `1px solid ${S.border}`, color: S.cream, padding: '4px 14px', cursor: saving ? 'default' : 'pointer', fontFamily: S.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={11} />{saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
